/**
 * Migration: apartments + legacy user/group fields → modern groups + groupMembers.
 * Ensures data consistency without breaking existing user or match data.
 *
 * Expected structure (confirmed before migration):
 *
 * groups/{groupId}
 *   id, name, shortCode, type (apartment|club|custom), isOpen, adminIds[], createdBy, createdAt,
 *   flatNumbers[] (apartment), avatarURL, description, phone, address, mapUrl,
 *   memberIds[] (user IDs with any groupMembers doc for this group),
 *   membershipIds[] (groupMembers doc ids: userId_groupId).
 *
 * groupMembers/{membershipId}  where membershipId = userId_groupId
 *   userId, groupId, approved, memberId (e.g. flat number), joinedAt, approvedBy?, approvedAt?
 *
 * users/{userId}
 *   approvedGroupIds[] (synced from groupMembers where approved=true); legacy: apartmentId, groupId, flatNumber, etc.
 *
 * Migration steps:
 * 1. Apartments → Groups: For each apartment, ensure a group exists with id=apartment.id, type=apartment.
 * 2. Legacy user → groupMembers: For each user with apartmentId or groupId but no groupMembers entry, create it.
 * 3. Sync user.approvedGroupIds for affected users.
 * 4. Sync each group's memberIds and membershipIds from groupMembers (so groups are managed well).
 *
 * Does NOT remove or overwrite any user fields (apartmentId, groupId, etc.).
 */

import {
  collection,
  getDocs,
  getDoc,
  doc,
  setDoc,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { syncUserApprovedGroupIds, syncGroupMemberIds } from './userGroupSyncService';
import { GROUP_TYPES } from '../constants/groups';

const GROUP_MEMBERS_ID = (userId, groupId) => `${userId}_${groupId}`;

/**
 * Ensure a group document exists for this apartment (same id, type=apartment).
 * Uses setDoc with merge so existing group data is preserved.
 */
async function ensureGroupFromApartment(apartment) {
  if (!apartment?.id) return { created: false, id: null };
  const groupRef = doc(db, 'groups', apartment.id);
  const existing = await getDoc(groupRef);
  const payload = {
    type: GROUP_TYPES.APARTMENT,
    name: apartment.name || apartment.id,
    shortCode: (apartment.shortCode || '').trim() || '',
    flatNumbers: Array.isArray(apartment.flatNumbers) ? apartment.flatNumbers : [],
    adminIds: Array.isArray(apartment.adminIds) ? apartment.adminIds : [],
    avatarURL: (apartment.avatarURL || '').trim() || null,
    description: (apartment.description || '').trim() || null,
    phone: (apartment.phone || '').trim() || null,
    address: (apartment.address || '').trim() || null,
    mapUrl: (apartment.mapUrl || '').trim() || null,
  };
  if (existing.exists()) {
    await setDoc(groupRef, payload, { merge: true });
    return { created: false, id: apartment.id };
  }
  await setDoc(groupRef, {
    ...payload,
    createdBy: (apartment.adminIds && apartment.adminIds[0]) || null,
    createdAt: new Date(),
    isOpen: false,
  });
  return { created: true, id: apartment.id };
}

/**
 * Ensure groupMembers doc exists for this user+groupId (from legacy user.apartmentId or user.groupId).
 */
async function ensureGroupMember(userId, groupId, { approved, memberId } = {}) {
  const id = GROUP_MEMBERS_ID(userId, groupId);
  const ref = doc(db, 'groupMembers', id);
  const existing = await getDoc(ref);
  if (existing.exists()) return { created: false };
  await setDoc(ref, {
    userId,
    groupId,
    approved: approved === true || approved === 'true',
    memberId: memberId || null,
    joinedAt: new Date(),
  });
  return { created: true };
}

/** Expected shapes for confirmation. */
export const GROUPS_AND_MEMBERSHIP_STRUCTURE = {
  groups: {
    collection: 'groups',
    docId: 'groupId',
    fields: ['id', 'name', 'shortCode', 'type', 'isOpen', 'adminIds', 'createdBy', 'createdAt', 'flatNumbers', 'avatarURL', 'description', 'phone', 'address', 'mapUrl', 'memberIds', 'membershipIds'],
    memberIds: 'array of user IDs with a groupMembers doc for this group',
    membershipIds: 'array of groupMembers doc ids (userId_groupId)',
  },
  groupMembers: {
    collection: 'groupMembers',
    docId: 'userId_groupId',
    fields: ['userId', 'groupId', 'approved', 'memberId', 'joinedAt', 'approvedBy', 'approvedAt'],
  },
  users: {
    collection: 'users',
    approvedGroupIds: 'array of group IDs where user has approved groupMembers entry',
    legacy: ['apartmentId', 'groupId', 'flatNumber', 'groupMemberId', 'groupApproved', 'apartmentApproved'],
  },
};

/**
 * Confirm structure of groups and group membership before migration.
 * Returns counts and sample doc shapes (field names only) for validation.
 */
export async function confirmGroupsAndMembershipStructure() {
  const result = {
    structure: GROUPS_AND_MEMBERSHIP_STRUCTURE,
    counts: { groups: 0, groupMembers: 0, users: 0, apartments: 0 },
    sampleGroupKeys: [],
    sampleGroupMemberKeys: [],
    sampleUserKeys: [],
    ok: true,
    error: null,
  };
  try {
    const [groupsSnap, membersSnap, usersSnap, apartmentsSnap] = await Promise.all([
      getDocs(collection(db, 'groups')),
      getDocs(collection(db, 'groupMembers')),
      getDocs(collection(db, 'users')),
      getDocs(collection(db, 'apartments')),
    ]);
    result.counts.groups = groupsSnap.size;
    result.counts.groupMembers = membersSnap.size;
    result.counts.users = usersSnap.size;
    result.counts.apartments = apartmentsSnap.size;
    if (groupsSnap.docs[0]) result.sampleGroupKeys = Object.keys(groupsSnap.docs[0].data());
    if (membersSnap.docs[0]) result.sampleGroupMemberKeys = Object.keys(membersSnap.docs[0].data());
    if (usersSnap.docs[0]) result.sampleUserKeys = Object.keys(usersSnap.docs[0].data());
  } catch (e) {
    result.ok = false;
    result.error = e?.message || String(e);
  }
  return result;
}

/**
 * Run full migration. Returns summary. Safe to run multiple times (idempotent).
 */
export async function runApartmentsAndLegacyMigration() {
  const summary = {
    apartmentsProcessed: 0,
    groupsCreated: 0,
    groupsUpdated: 0,
    usersProcessed: 0,
    groupMembersCreated: 0,
    approvedGroupIdsSynced: 0,
    approvedGroupIdsPermissionDenied: 0,
    groupsMemberIdsSynced: 0,
    errors: [],
  };

  try {
    // 1. Apartments → Groups
    const apartmentsSnap = await getDocs(collection(db, 'apartments'));
    const apartments = apartmentsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    for (const apt of apartments) {
      try {
        const result = await ensureGroupFromApartment(apt);
        summary.apartmentsProcessed += 1;
        if (result.created) summary.groupsCreated += 1;
        else summary.groupsUpdated += 1;
      } catch (e) {
        summary.errors.push(`apartment ${apt.id}: ${e?.message || e}`);
      }
    }

    // 2. Users with legacy apartmentId or groupId → groupMembers
    const usersSnap = await getDocs(collection(db, 'users'));
    const users = usersSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    const userIdsToSyncApproved = new Set();

    for (const user of users) {
      summary.usersProcessed += 1;
      try {
        if (user.apartmentId) {
          const mid = GROUP_MEMBERS_ID(user.id, user.apartmentId);
          const memSnap = await getDoc(doc(db, 'groupMembers', mid));
          if (!memSnap.exists()) {
            await ensureGroupMember(user.id, user.apartmentId, {
              approved: user.apartmentApproved,
              memberId: user.flatNumber || user.groupMemberId,
            });
            summary.groupMembersCreated += 1;
            userIdsToSyncApproved.add(user.id);
          }
        }
        if (user.groupId && user.groupId !== user.apartmentId) {
          const mid = GROUP_MEMBERS_ID(user.id, user.groupId);
          const memSnap = await getDoc(doc(db, 'groupMembers', mid));
          if (!memSnap.exists()) {
            await ensureGroupMember(user.id, user.groupId, {
              approved: user.groupApproved,
              memberId: user.groupMemberId || user.flatNumber,
            });
            summary.groupMembersCreated += 1;
            userIdsToSyncApproved.add(user.id);
          }
        }
      } catch (e) {
        summary.errors.push(`user ${user.id}: ${e?.message || e}`);
      }
    }

    // 3. Sync approvedGroupIds for users we added groupMembers for (requires super admin to update other users)
    for (const uid of userIdsToSyncApproved) {
      try {
        await syncUserApprovedGroupIds(uid);
        summary.approvedGroupIdsSynced += 1;
      } catch (e) {
        const msg = e?.message || String(e);
        const isPermission = /permission|insufficient/i.test(msg);
        if (isPermission) {
          summary.approvedGroupIdsPermissionDenied += 1;
        } else {
          summary.errors.push(`sync approvedGroupIds ${uid}: ${msg}`);
        }
      }
    }

    // 4. Sync each group's memberIds and membershipIds from groupMembers
    const groupsSnap = await getDocs(collection(db, 'groups'));
    for (const d of groupsSnap.docs) {
      try {
        await syncGroupMemberIds(d.id);
        summary.groupsMemberIdsSynced += 1;
      } catch (e) {
        summary.errors.push(`sync group memberIds ${d.id}: ${e?.message || e}`);
      }
    }
  } catch (e) {
    summary.errors.push(`migration: ${e?.message || e}`);
  }

  return summary;
}
